import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getRealm } from "@/AuthProvider";
import PasswordInput from "@/components/app/PasswordInput";
import { appPaths } from "@/constants/paths";
import { CHANGE_PASSWORD_FORM_INITIAL_VALUES } from "@/constants/setting-initial-values";
import { useQueryParams } from "@/hooks/use-query-param";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useResetPassword } from "@/queries/use-reset-password";
import { getErrorSnackbar, getSuccessSnackbar } from "@/utils/utils";
import {
  isPasswordValid,
  setPasswordValidationSchema,
} from "@/utils/password-validation";
import { LOGIN_TOKENS } from "@/tokens/design-tokens";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import PasswordRequirements from "@/components/app/PasswordRequirements";

interface Props {
  linkType: "set" | "reset";
}

const SetAndResetPassword = ({ linkType }: Props) => {
  const showSnackbar = useSnackbar();

  const { mutateAsync: resetPasswordAPI } = useResetPassword();
  const navigate = useNavigate();
  const expiryCode = useQueryParams("expiryCode");
  // const tenant = useQueryParams("tenant");

  const env = import.meta.env.VITE_ENV_NAME;
  const hostNameArr = location.hostname?.split(".");
  const portal =
    env === "prod" && hostNameArr?.[0] === "app" ? "admin" : hostNameArr[2];

  // todo need to remove
  const realm = getRealm(hostNameArr);

  const {
    values,
    touched,
    errors,
    handleBlur,
    submitForm,
    setFieldValue,
    setTouched,
  } = useFormik({
    initialValues: CHANGE_PASSWORD_FORM_INITIAL_VALUES,
    validationSchema: setPasswordValidationSchema,
    onSubmit: handleSave,
  });

  const isFormValid =
    isPasswordValid(values.newPassword) &&
    values.confirmPassword === values.newPassword;

  function handleSave(values: any) {
    setTouched(values, true);
    const payload = {
      linkType: linkType,
      ResetPasswordRequest: {
        newPassword: values.newPassword?.trim(),
        expiryCode: expiryCode ?? "",
      },
      xTENANTID: realm,
    };

    resetPasswordAPI(payload)
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        if (hostNameArr[0] === "localhost") {
          navigate(
            `${appPaths.login}?realm=${portal === "admin" ? undefined : realm}`
          );
        } else navigate(appPaths.login);
        //todo need to remove
        location.reload();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  }

  // todo need to remove
  useEffect(() => {}, [errors, touched]);

  return (
    <Box sx={{ width: "100%" }}>
      {/* Heading */}
      <Box sx={{ mb: "32px", width: "100%" }}>
        <Typography
          sx={{
            color: LOGIN_TOKENS.color.primary,
            fontSize: LOGIN_TOKENS.fontSize.heading,
            fontFamily: LOGIN_TOKENS.fontFamily,
            fontWeight: LOGIN_TOKENS.fontWeight.medium,
            lineHeight: 1.2,
          }}
        >
          {linkType === "set" ? "Set New Password" : "Reset Password"}
        </Typography>
      </Box>

      {/* Form Fields */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <PasswordInput
          label="New Password"
          placeholder="**********"
          name="newPassword"
          onBlur={handleBlur}
          value={values.newPassword}
          onChange={(value) => {
            setFieldValue("newPassword", value);
          }}
          required
          error={touched.newPassword && (errors.newPassword as any)}
        />
        <PasswordRequirements password={values.newPassword} />
        <PasswordInput
          label="Confirm New Password"
          placeholder="**********"
          name="confirmPassword"
          onBlur={handleBlur}
          value={values.confirmPassword}
          onChange={(value) => {
            setFieldValue("confirmPassword", value);
          }}
          required
          error={(touched.confirmPassword && errors.confirmPassword) as any}
        />
      </Box>

      {/* Submit Button */}
      <Box sx={{ width: "100%", mt: "32px" }}>
        <StyledButtonV2
          label={linkType === "set" ? "Set New Password" : "Update Password"}
          variant="contained"
          fullWidth
          type="submit"
          disabled={!isFormValid}
          onClick={submitForm}
          sx={{
            py: "16px",
            px: "18px",
            fontSize: LOGIN_TOKENS.fontSize.button,
            fontFamily: LOGIN_TOKENS.fontFamily,
            fontWeight: LOGIN_TOKENS.fontWeight.medium,
            textTransform: "none",
            borderRadius: "4px",
            backgroundColor: LOGIN_TOKENS.color.primary,
            border: `1px solid ${LOGIN_TOKENS.color.primary}`,
            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
            lineHeight: 1.25,
            "&:hover": { backgroundColor: LOGIN_TOKENS.color.primaryHover },
            height: LOGIN_TOKENS.height,
          }}
        />
      </Box>
    </Box>
  );
};

export default SetAndResetPassword;
